import axios from "axios";

import config from "../config";
import { clearAuthTokens, getAuthToken, getRefreshToken, saveAuthTokens } from "./auth";

export const axiosInstance = axios.create({
  baseURL: config.apiURL,
  headers: {
    "X-SRL-Env": config.srlEnv,
  },
});

// Attach Authorization header if we have a token cookie
axiosInstance.interceptors.request.use((requestConfig) => {
  const token = getAuthToken();

  // eslint-disable-next-line no-param-reassign
  requestConfig.headers = {
    ...requestConfig.headers,
    "X-SRL-Env": config.srlEnv,
  };

  if (token && !requestConfig.headers?.Authorization) {
    // eslint-disable-next-line no-param-reassign
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }

  return requestConfig;
});

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(newToken) {
  console.log(
    "[DEBUG_LOG] onTokenRefreshed called with token:",
    newToken ? "present" : "null",
    "subscribers count:",
    refreshSubscribers.length,
  );
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config: originalRequest } = error || {};

    if (!response) {
      // Network / CORS / other error without a response
      return Promise.reject(error);
    }

    if (response.status !== 401 || originalRequest._retry) {
      if (response.status === 401 && originalRequest._retry) {
        console.log(
          "[DEBUG_LOG] Request already retried, skipping interceptor for:",
          originalRequest.url,
        );
      }
      return Promise.reject(error);
    }

    // Mark the request so we don't retry it infinitely
    // eslint-disable-next-line no-underscore-dangle
    originalRequest._retry = true;

    console.log(
      "[DEBUG_LOG] 401 Interceptor triggered for:",
      originalRequest.url,
      "isRefreshing:",
      isRefreshing,
    );

    if (!isRefreshing) {
      isRefreshing = true;

      const retryPromise = new Promise((resolve, reject) => {
        console.log("[DEBUG_LOG] Queuing request:", originalRequest.url);
        subscribeTokenRefresh((newToken) => {
          console.log(
            "[DEBUG_LOG] Subscriber triggered for:",
            originalRequest.url,
            "newToken:",
            newToken ? "present" : "null",
          );
          if (!newToken) {
            console.log("[DEBUG_LOG] No new token for queued request:", originalRequest.url);

            reject(error);
            return;
          }
          console.log("[DEBUG_LOG] Retrying request with new token:", originalRequest.url);

          const newHeaders = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${newToken}`,
          };

          const requestConfig = {
            ...originalRequest,
            headers: newHeaders,
          };

          resolve(axiosInstance.request(requestConfig));
        });
      });

      try {
        const refreshToken = getRefreshToken();
        const refreshResponse = await axiosInstance.post(
          `/auth/discord/refresh${refreshToken ? `?token=${refreshToken}` : ""}`,
        );

        const data = refreshResponse?.data || {};
        const { token } = data;

        if (!token) {
          console.log("[DEBUG_LOG] No token in refresh response");
          clearAuthTokens();
          isRefreshing = false;
          onTokenRefreshed(null);
          return;
        }

        // Save new tokens to cookies
        saveAuthTokens(data);

        console.log("[DEBUG_LOG] Refresh successful, calling onTokenRefreshed");
        isRefreshing = false;
        onTokenRefreshed(token);
      } catch (refreshError) {
        console.log("[DEBUG_LOG] Refresh failed:", refreshError.message);
        clearAuthTokens();
        isRefreshing = false;
        onTokenRefreshed(null);
      }

      return retryPromise;
    }
  },
);

export const axiosCMSInstance = axios.create({
  baseURL: `${config.csmURL}/api`,
  headers: {
    Authorization: `Bearer ${config.cmsApiToken}`,
  },
});

export const axiosNSInsance = axios.create({
  //node service -> NS - no idead for better name
  baseURL: `${config.nodeService}/api`,
  // headers: {
  //   Authorization: `Bearer ${getAuthToken()}`,
  // },
});

axiosNSInsance.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error(
      "[CMS] Request failed:",
      error?.response?.status,
      error?.config?.url,
      error?.message,
    );
    return Promise.resolve(null);
  },
);

export { saveAuthTokens, getAuthToken, getRefreshToken, clearAuthTokens } from "./auth";
